import type {
  ActionSpec,
  AppSpec,
  AppStruct,
  PerkIntegrationSpec,
  TriggerIntegrationSpec,
} from '@cohort/shared/schema/apps/app';
import {ListConnectedPagesActionSpec} from '@cohort/shared/schema/apps/instagram/actions/listConnectedPages';
import {ListMediasActionSpec} from '@cohort/shared/schema/apps/instagram/actions/listMedias';
import type {InstagramMerchantConnectorStruct} from '@cohort/shared/schema/apps/instagram/merchantConnector';
import {InstagramMerchantConnectorSpec} from '@cohort/shared/schema/apps/instagram/merchantConnector';
import {InstagramCloseFriendsListPerkIntegrationSpec} from '@cohort/shared/schema/apps/instagram/perks/closeFriendsList';
import {InstagramCommentMediaTriggerIntegrationSpec} from '@cohort/shared/schema/apps/instagram/triggers/commentMedia';
import {InstagramConnectAccountTriggerIntegrationSpec} from '@cohort/shared/schema/apps/instagram/triggers/connectAccount';
import {InstagramTagInPostTriggerIntegrationSpec} from '@cohort/shared/schema/apps/instagram/triggers/tagInPost';
import type {InstagramUserConnectorStruct} from '@cohort/shared/schema/apps/instagram/userConnector';
import {InstagramUserConnectorSpec} from '@cohort/shared/schema/apps/instagram/userConnector';
import type {UserPropertySpec, UserPropertyStruct} from '@cohort/shared/schema/apps/userProperty';
import {
  createAppUsernameUserPropertySpec,
  createBaseAppUserPropertySpec,
} from '@cohort/shared/schema/apps/userProperty';

export const INSTAGRAM_APP_ID = 'instagram' as const;
export const INSTAGRAM_APP_NAME = 'Instagram' as const;

export const InstagramUserPropertiesSpecs = [
  ...createBaseAppUserPropertySpec(INSTAGRAM_APP_ID, INSTAGRAM_APP_NAME),
  createAppUsernameUserPropertySpec(INSTAGRAM_APP_ID, INSTAGRAM_APP_NAME),
  {
    id: 'instagram.media-count',
    dataType: 'number',
    name: 'Instagram Number of Media',
    scope: 'profile',
    profileKey: 'mediaCount',
  },
] as const satisfies ReadonlyArray<UserPropertySpec<UserPropertyStruct>>;
type InstagramUserPropertyIds = (typeof InstagramUserPropertiesSpecs)[number]['id'];

export const InstagramActionSpecs = [
  ListConnectedPagesActionSpec,
  ListMediasActionSpec,
] as const satisfies ReadonlyArray<ActionSpec>;
type InstagramActionIds = (typeof InstagramActionSpecs)[number]['id'];

export const InstagramPerkIntegrationSpecs = [
  InstagramCloseFriendsListPerkIntegrationSpec,
] as const satisfies ReadonlyArray<PerkIntegrationSpec>;
type InstagramPerkIntegrationIds = (typeof InstagramPerkIntegrationSpecs)[number]['id'];

export const InstagramTriggerIntegrationSpecs = [
  InstagramCommentMediaTriggerIntegrationSpec,
  InstagramConnectAccountTriggerIntegrationSpec,
  InstagramTagInPostTriggerIntegrationSpec,
] as const satisfies ReadonlyArray<TriggerIntegrationSpec>;
type InstagramTriggerIntegrationIds = (typeof InstagramTriggerIntegrationSpecs)[number]['id'];

export type InstagramAppStruct = AppStruct<
  'instagram',
  InstagramMerchantConnectorStruct,
  InstagramUserConnectorStruct,
  null,
  InstagramActionIds,
  never,
  InstagramPerkIntegrationIds,
  InstagramTriggerIntegrationIds,
  never,
  InstagramUserPropertyIds,
  never
>;

export const InstagramAppSpec: AppSpec<InstagramAppStruct> = {
  id: INSTAGRAM_APP_ID,
  name: INSTAGRAM_APP_NAME,
  merchantConnector: InstagramMerchantConnectorSpec,
  userConnector: InstagramUserConnectorSpec,
  usersSyncSpec: null,
  actionSpecs: InstagramActionSpecs,
  notificationIntegrationSpecs: [],
  perkIntegrationSpecs: InstagramPerkIntegrationSpecs,
  triggerIntegrationSpecs: InstagramTriggerIntegrationSpecs,
  userEventSpecs: [],
  userPropertySpecs: InstagramUserPropertiesSpecs,
  resourceSpecs: [],
};
